import React from 'react'

import { WidgetComponent } from 'features/views/LayoutEditor/types'
import { FieldCollection } from 'features/views/LayoutEditor/widgets/common/FieldCollection/FieldCollection'
import { RecordManagerContextProvider } from 'features/views/RecordManager/RecordManagerContext'

import { Box } from 'ui/components/Box'
import { Button, ButtonProps } from 'ui/components/Button'
import { Dropdown, DropdownButton, DropdownContent, DropdownItem } from 'ui/components/Dropdown'
import { LinkButton, LinkButtonProps } from 'ui/components/LinkButton'
import { Skeleton } from 'ui/components/Skeleton'
import { Tag } from 'ui/components/Tag'
import { Body, Headline } from 'ui/components/Text'
import { theme } from 'ui/styling/Theme.css'

import { DescriptionAttribute } from './attributes/DescriptionAttribute'
import { ProfileImageAttribute } from './attributes/ProfileImageAttribute'
import { SubtitleAttribute } from './attributes/SubtitleAttribute'
import { TitleAttribute } from './attributes/TitleAttribute'
import { useRelatedRecordWidgetActionButtonState } from './hooks/useRelatedRecordWidgetActionButtonState'
import { useRelatedRecordWidgetActionsState } from './hooks/useRelatedRecordWidgetActionsState'
import { useRelatedRecordWidgetState } from './hooks/useRelatedRecordWidgetState'
import { useRelatedRecordWidgetWrapperState } from './hooks/useRelatedRecordWidgetWrapperState'
import { RelatedRecordWidgetFieldItem, RelatedRecordWidgetType } from './types'

const DESCRIPTION_MAX_LINES = 3
const DESCRIPTION_MAX_LINES_SMALL = 6

type RelatedRecordWidgetProps = {}

const RelatedRecordWidgetComponent: WidgetComponent<
    RelatedRecordWidgetType,
    RelatedRecordWidgetProps
> = ({ widget, isEditing }) => {
    const {
        isLoadingSlow,
        hasField,
        hideWidget,
        targetRecord,
        targetObject,
        titleField,
        titleValue,
        profileImageField,
        profileImageValue,
        subtitleField,
        subtitleValue,
        labelValue,
        descriptionField,
        descriptionValue,
        isError,
        visibleFields,
        showFieldIcon,
        alignment,
        ref,
        isSmall,
        labelPlacement,
    } = useRelatedRecordWidgetState({
        widget,
    })

    if (hideWidget) return null

    if (!hasField && isEditing) {
        return (
            <Box flex column gap="m" py="l" center={alignment === 'center'} width="full">
                <Body color="textWeakest">No field selected</Body>
            </Box>
        )
    }

    if (isError) {
        return (
            <Box
                flex
                flexDirection="column"
                alignSelf="stretch"
                py="l"
                center={alignment === 'center'}
                width="full"
            >
                <Body size="m" color="textError">
                    Something&apos;s wrong. Could not load related record for widget.
                </Body>
            </Box>
        )
    }

    return (
        <Box py="l" flex column center={alignment === 'center'} ref={ref} width="full">
            <RelatedRecordWidgetHeader
                isLoading={isLoadingSlow}
                titleField={titleField}
                titleValue={titleValue}
                profileImageField={profileImageField}
                profileImageValue={profileImageValue}
                subtitleField={subtitleField}
                subtitleValue={subtitleValue}
                labelValue={labelValue}
                isSmall={isSmall}
                alignment={alignment}
            />
            {visibleFields.length > 0 && (
                <RelatedRecordWidgetFields
                    visibleFields={visibleFields}
                    showFieldIcon={showFieldIcon}
                    labelPlacement={labelPlacement}
                    alignment={alignment}
                />
            )}
            {descriptionField && (
                <DescriptionAttribute
                    field={descriptionField}
                    value={descriptionValue}
                    isLoading={isLoadingSlow}
                    maxLines={isSmall ? DESCRIPTION_MAX_LINES_SMALL : DESCRIPTION_MAX_LINES}
                    mt="xl"
                    size="m"
                    color="text"
                    alignment={alignment}
                />
            )}
            <RelatedRecordWidgetActions
                widget={widget}
                isLoading={isLoadingSlow}
                record={targetRecord}
                object={targetObject}
                mt="3xl"
            />
        </Box>
    )
}

const RelatedRecordWidgetComponentWrapper: WidgetComponent<
    RelatedRecordWidgetType,
    RelatedRecordWidgetProps
> = ({ widget, isEditing, ...props }) => {
    const { targetRecordSid, showSuccessToast } = useRelatedRecordWidgetWrapperState(widget)

    if (!targetRecordSid) return null

    return (
        <RecordManagerContextProvider
            recordSid={targetRecordSid}
            supportUnsavedChangesModal={true}
            showSuccessToast={showSuccessToast}
        >
            <RelatedRecordWidgetComponent widget={widget} isEditing={isEditing} {...props} />
        </RecordManagerContextProvider>
    )
}

export const RelatedRecordWidget = React.memo(RelatedRecordWidgetComponentWrapper)

type RelatedRecordWidgetHeaderProps = {
    titleField?: FieldDto
    titleValue?: unknown
    profileImageField?: FieldDto
    profileImageValue?: unknown
    subtitleField?: FieldDto
    subtitleValue?: unknown
    labelValue?: string
    isLoading?: boolean
    isSmall?: boolean
    alignment?: RelatedRecordWidgetType['attrs']['alignment']
}

const RelatedRecordWidgetHeader: React.FC<RelatedRecordWidgetHeaderProps> = ({
    titleField,
    titleValue,
    profileImageField,
    profileImageValue,
    subtitleField,
    subtitleValue,
    labelValue,
    isLoading,
    isSmall,
    alignment,
}) => {
    const isTextCentred = alignment === 'center' && isSmall
    const isContentCentred = isTextCentred || !isSmall

    return (
        <Box
            flex
            center={isContentCentred}
            gap="xl"
            maxWidth="full"
            minWidth={0}
            column={isSmall}
            textAlign={isTextCentred ? 'center' : undefined}
        >
            {profileImageField && (
                <Box noShrink>
                    <Skeleton
                        isLoading={isLoading}
                        minWidth={0}
                        maxWidth="full"
                        as="div"
                        style={{
                            borderRadius: theme.radius.pill,
                        }}
                    >
                        <ProfileImageAttribute
                            profileImageValue={profileImageValue}
                            profileImageField={profileImageField}
                            titleValue={titleValue}
                            titleField={titleField}
                            isLoading={isLoading}
                            size={isSmall ? '3xl' : '2xl'}
                        />
                    </Skeleton>
                </Box>
            )}
            <Box grow shrink flex column gap="s" maxWidth="full" minWidth={0}>
                {labelValue && (
                    <Box minWidth={0} maxWidth="full" lineHeight={0}>
                        <Skeleton isLoading={isLoading} minWidth={0} maxWidth="full">
                            <Tag
                                size="2xs"
                                shade="light"
                                type="solid"
                                shape="rectangle"
                                style={{ wordBreak: 'break-word' }}
                                pointerEvents="none"
                                maxWidth="full"
                            >
                                {labelValue}
                            </Tag>
                        </Skeleton>
                    </Box>
                )}
                {titleField && (
                    <Box minWidth={0} maxWidth="full" lineHeight={0}>
                        <Headline size="s" display="inline-block" maxWidth="full" minWidth={0}>
                            <TitleAttribute
                                field={titleField}
                                value={titleValue}
                                isLoading={isLoading}
                                style={{
                                    fontSize: 'inherit',
                                    fontWeight: 'inherit',
                                    lineHeight: 'inherit',
                                }}
                            />
                        </Headline>
                    </Box>
                )}
                {subtitleField && (
                    <Box minWidth={0} maxWidth="full" lineHeight={0}>
                        <Box display="inline-block" minWidth={0} maxWidth="full">
                            <SubtitleAttribute
                                field={subtitleField}
                                value={subtitleValue}
                                isLoading={isLoading}
                                size="m"
                                color="textWeaker"
                            />
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

type RelatedRecordWidgetFieldsProps = {
    visibleFields: RelatedRecordWidgetFieldItem[]
    alignment?: RelatedRecordWidgetType['attrs']['alignment']
    showFieldIcon?: RelatedRecordWidgetType['attrs']['showFieldIcon']
    labelPlacement?: RelatedRecordWidgetType['attrs']['labelPlacement']
}

const RelatedRecordWidgetFields: React.FC<RelatedRecordWidgetFieldsProps> = ({
    visibleFields,
    showFieldIcon,
    labelPlacement,
    alignment,
}) => {
    return (
        <Box mt="xl" width="full" justifyContent={alignment === 'center' ? 'center' : 'flex-start'}>
            <FieldCollection
                fields={visibleFields}
                labelPlacement={labelPlacement}
                showFieldIcon={showFieldIcon}
            />
        </Box>
    )
}

type RelatedRecordWidgetActionsProps = React.ComponentPropsWithoutRef<typeof Box> & {
    widget: RelatedRecordWidgetType
    object?: ObjectDto
    record?: RecordDto
    isLoading?: boolean
}

const RelatedRecordWidgetActions: React.FC<RelatedRecordWidgetActionsProps> = ({
    widget,
    object,
    record,
    isLoading,
    style,
    ...props
}) => {
    const { visibleActions, menuActions, gridTemplateColumns, minWidth, shouldShow } =
        useRelatedRecordWidgetActionsState({ widget, object, record })

    if (!shouldShow) return null

    return (
        <Box
            display="grid"
            gap="m"
            style={{
                gridTemplateColumns,
                minWidth,
                zIndex: 1,
                ...style,
            }}
            {...props}
        >
            {visibleActions.map((action, idx) => (
                <Skeleton key={action._sid} isLoading={isLoading}>
                    <RelatedRecordWidgetActionButton
                        key={action._sid}
                        action={action}
                        size="m"
                        variant={idx === 0 ? 'primary' : 'secondary'}
                    />
                </Skeleton>
            ))}
            {menuActions.length > 0 && (
                <Skeleton isLoading={isLoading}>
                    <Dropdown>
                        <DropdownButton
                            size="m"
                            variant="secondary"
                            startIcon={{ name: 'MoreHorizontal' }}
                            noShrink
                        />
                        <DropdownContent align="end" side="bottom" style={{ minWidth: '286px' }}>
                            {menuActions.map((action) => (
                                <RelatedRecordWidgetActionDropdownItem
                                    key={action._sid}
                                    action={action}
                                    closeOnSelect
                                />
                            ))}
                        </DropdownContent>
                    </Dropdown>
                </Skeleton>
            )}
        </Box>
    )
}

type RelatedRecordWidgetActionButtonProps = React.ComponentPropsWithoutRef<typeof Button> & {
    action: ActionDto
}

export const RelatedRecordWidgetActionButton: React.FC<RelatedRecordWidgetActionButtonProps> = ({
    action,
    ...props
}) => {
    const { handleClick, to, href, target, isLink } = useRelatedRecordWidgetActionButtonState({
        action,
    })

    return (
        <Box
            as={
                (isLink ? LinkButton : Button) as React.ComponentType<
                    ButtonProps & Pick<LinkButtonProps, 'to' | 'href' | 'target'>
                >
            }
            {...props}
            to={to as any}
            href={href as any}
            target={target}
            onClick={handleClick}
        >
            <Box
                trim
                maxWidth="full"
                style={{
                    paddingTop: '2px',
                    paddingBottom: '2px',
                    marginTop: '-2px',
                    marginBottom: '-2px',
                }}
            >
                {action.name}
            </Box>
        </Box>
    )
}

type RelatedRecordWidgetActionDropdownItemProps = React.ComponentPropsWithoutRef<
    typeof DropdownItem
> & {
    action: ActionDto
}

export const RelatedRecordWidgetActionDropdownItem: React.FC<
    RelatedRecordWidgetActionDropdownItemProps
> = ({ action, ...props }) => {
    const { handleClick } = useRelatedRecordWidgetActionButtonState({ action })

    return <DropdownItem label={action.name} {...props} onClick={() => handleClick()} />
}
